import {Component, ElementRef, HostListener, OnDestroy, OnInit, signal} from '@angular/core';
import {ToggleService} from './toggle.service';
import {CommonModule, NgClass} from '@angular/common';
import {Router, RouterLink, NavigationEnd} from '@angular/router';
import {MatMenuModule} from '@angular/material/menu';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatBadgeModule} from '@angular/material/badge';
import {MatDialog} from '@angular/material/dialog';
import {ApiAuthServices} from '../../services/auth.service';
import {Subscription, filter} from 'rxjs';
import {
    ChangePasswordDialogComponent
} from '../../components/change-password-dialog/change-password-dialog.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../core/services/language.service';
import { PERMISSION_CODES } from '../../shared/constants/auth.constant';
import { NotificationService } from '../../services/notification.service';

@Component({
    selector: 'app-header',
    imports: [
        CommonModule,
        RouterLink,
        NgClass,
        MatMenuModule,
        MatIconModule,
        MatButtonModule,
        MatBadgeModule,
        TranslateModule
    ],
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {

    isToggled = false;
    currentLanguage: string = 'en';
    currentLanguageFlag = 'img/flags/en.png';
    userInfo: any = null;
    private langChangeSubscription?: Subscription;
    private routerEventsSub?: Subscription;
    avatarError = false;
    permission = PERMISSION_CODES;
    notificationInbox: any[] = [];
    showNotificationPanel = false;
    chatUnreadCount = signal(0);
    isChatActive = false;

    constructor(
        private toggleService: ToggleService,
        public authService: ApiAuthServices,
        private dialog: MatDialog,
        private translate: TranslateService,
        private languageService: LanguageService,
        private router: Router,
        public notificationService: NotificationService,
        private elementRef: ElementRef<HTMLElement>,
    ) {
        this.toggleService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
        this.currentDate = new Date();
        this.currentLanguage = this.languageService.getCurrentLanguage();
        this.updateFlag();

        // Subscribe to language changes (translate.currentLang is NOT a signal, effect() won't work)
        this.langChangeSubscription = this.translate.onLangChange.subscribe(event => {
            this.currentLanguage = event.lang;
            this.updateFlag();
        });
    }

    ngOnInit(): void {
        this.userInfo = this.authService.getUserInfo();
        this.routerEventsSub = this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e) => {
                this.avatarError = false;
                this.showNotificationPanel = false;
                this.isChatActive = e.urlAfterRedirects.startsWith('/chat');
            });
        this.notificationService.fetchUnreadCount().subscribe();
    }

    ngOnDestroy(): void {
        if (this.langChangeSubscription) {
            this.langChangeSubscription.unsubscribe();
        }
        this.routerEventsSub?.unsubscribe();
    }

    canShowCompanyProfile(): boolean {
        return this.authService.isBusiness() || this.authService.hasPermission(this.permission.PROFILE_COMPANY.PROFILE);
    }

    /** Translation key for user role under name in profile menu */
    getUserRoleTranslationKey(): string {
        if (this.authService.isEnterpriseUser()) {
            return 'header.roleBusiness';
        }
        if (this.authService.isManager()) {
            return 'header.roleManager';
        }
        return 'header.roleEditor';
    }

    getFullName(): string {
        return this.authService.getHeaderDisplayLabel();
    }

    getDepartmentName(): string | null {
        if (this.authService.isEnterpriseUser()) {
            return null;
        }
        const departmentCode = this.authService.getDepartmentCode()?.trim();
        if (!departmentCode) {
            return this.authService.getDepartmentName();
        }

        const translationKey = `user.departments.${departmentCode}`;
        const translated = this.translate.instant(translationKey);
        return translated && translated !== translationKey
            ? translated
            : this.authService.getDepartmentName() || departmentCode;
    }

    canShowStaffProfile(): boolean {
        return this.authService.isVcciStaff();
    }

    getAvatarUrl(): string | null {
        return this.authService.getHeaderAvatarUrl();
    }

    getAvatarInitial(): string {
        const fullname = this.getFullName();
        if (!fullname) return 'U';
        return fullname.charAt(0).toUpperCase();
    }

    hasAvatar(): boolean {
        return !!this.getAvatarUrl() && !this.avatarError;
    }

    onAvatarError(): void {
        this.avatarError = true;
    }

    updateFlag(): void {
        const flagMap: Record<string, string> = {
            'en': 'img/flags/en.png',
            'vi': 'img/flags/vi.png',
            'kr': 'img/flags/kr.png'
        };
        this.currentLanguageFlag = flagMap[this.currentLanguage] || 'img/flags/en.png';
    }

    currentDate: Date;

    changeLanguage(lang: string): void {
        this.languageService.switchLanguage(lang);
    }

    isCurrentLanguage(lang: string): boolean {
        return this.currentLanguage === lang;
    }

    toggle() {
        this.toggleService.toggle();
    }

    logout(): void {
        this.authService.logout();
    }

    toggleNotificationPanel(): void {
        this.showNotificationPanel = !this.showNotificationPanel;
        if (this.showNotificationPanel) {
            this.loadNotificationInbox();
        }
    }

    loadNotificationInbox(): void {
        this.notificationService.getMyNotifications(0, 10).subscribe({
            next: (res: any) => {
                this.notificationInbox = res?.result?.content ?? [];
            },
        });
    }

    openNotification(item: any): void {
        this.showNotificationPanel = false;
        if (item.readStatus === 'UNREAD') {
            this.notificationService.markRead(item.id).subscribe();
        }
        if (item.redirectUrl) {
            this.router.navigate([item.redirectUrl]);
        } else {
            this.router.navigate(['/notification-management']);
        }
    }

    markAllRead(event?: Event): void {
        event?.stopPropagation();
        this.notificationService.markAllRead().subscribe({
            next: () => this.loadNotificationInbox(),
        });
    }

    /** Close the notification panel when the user clicks anywhere outside the bell wrap. */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.showNotificationPanel) return;
        const target = event.target as HTMLElement | null;
        if (!target) return;
        const wrap = this.elementRef.nativeElement.querySelector('.notification-wrap');
        if (wrap && !wrap.contains(target)) {
            this.showNotificationPanel = false;
        }
    }

    /** Close the panel on Escape for accessibility. */
    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.showNotificationPanel) {
            this.showNotificationPanel = false;
        }
    }

    /** Material icon name based on notification type. */
    notificationIcon(type?: string): string {
        switch (type) {
            case 'SYSTEM': return 'campaign';
            case 'ACCOUNT': return 'person';
            case 'PERMISSION': return 'admin_panel_settings';
            case 'ENTERPRISE_APPROVAL': return 'business';
            case 'EVENT_APPROVAL': return 'event_available';
            case 'EVENT_REGISTRATION': return 'how_to_reg';
            case 'EVENT_REMINDER': return 'event';
            case 'SPONSOR_AD': return 'workspace_premium';
            case 'IMPORT_REQUEST': return 'local_shipping';
            default: return 'notifications';
        }
    }

    /** CSS modifier class for the icon avatar background, based on notification type. */
    notificationIconClass(type?: string): string {
        switch (type) {
            case 'SYSTEM': return 'notification-item__icon--system';
            case 'ACCOUNT':
            case 'PERMISSION': return 'notification-item__icon--account';
            case 'ENTERPRISE_APPROVAL': return 'notification-item__icon--enterprise';
            case 'EVENT_APPROVAL':
            case 'EVENT_REGISTRATION':
            case 'EVENT_REMINDER': return 'notification-item__icon--event';
            case 'SPONSOR_AD': return 'notification-item__icon--sponsor';
            case 'IMPORT_REQUEST': return 'notification-item__icon--import';
            default: return 'notification-item__icon--default';
        }
    }

    /** Localized relative time, e.g. "Just now", "5 min ago", "2h ago", "3d ago". */
    relativeTime(value?: string | Date | null): string {
        if (!value) return '';
        const date = typeof value === 'string' ? new Date(value) : value;
        const time = date?.getTime();
        if (!time || Number.isNaN(time)) return '';
        const diffSec = Math.floor((Date.now() - time) / 1000);
        if (diffSec < 60) {
            return this.translate.instant('header.notifications.timeJustNow');
        }
        const min = Math.floor(diffSec / 60);
        if (min < 60) {
            return this.translate.instant('header.notifications.timeMinAgo', { n: min });
        }
        const hr = Math.floor(min / 60);
        if (hr < 24) {
            return this.translate.instant('header.notifications.timeHourAgo', { n: hr });
        }
        const day = Math.floor(hr / 24);
        if (day < 7) {
            return this.translate.instant('header.notifications.timeDayAgo', { n: day });
        }
        return date.toLocaleDateString();
    }

    openChangePasswordDialog(): void {
        this.dialog.open(ChangePasswordDialogComponent, {
            width: '560px',
            maxWidth: '95vw',
            disableClose: true,
            panelClass: 'change-password-dialog-container'
        });
    }
}
